import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import ProjectTagField from "./projects/ProjectTagField";

const PHASE_TEXT = {
  reading: "Reading file…",
  uploading: "Uploading and processing… (this can take up to a minute for large files)",
};

export default function ReportUploadDialog({ isOpen, onClose, onUpload, projectTags, defaultProject = null }) {
  const [file, setFile] = useState(null);
  // Opened from a project's list, an upload goes to that project unless changed.
  const [projectTag, setProjectTag] = useState(defaultProject);
  useEffect(() => {
    if (isOpen) setProjectTag(defaultProject);
  }, [isOpen, defaultProject]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [phase, setPhase] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setErrorMessage("");
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!isUploading) setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (isUploading) return;
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setErrorMessage("");
    }
  };

  const closeAndReset = () => {
    setFile(null);
    setProjectTag(null);
    setPhase(null);
    setErrorMessage("");
    setIsUploading(false);
    onClose();
  };

  const handleUpload = async () => {
    if (!file) return;
    setIsUploading(true);
    setErrorMessage("");
    setPhase("reading");
    try {
      // ReportInputTable.handleFileUpload returns a Promise and accepts an
      // onPhase callback for the "reading" → "uploading" handoff.
      await onUpload(file, (p) => setPhase(p), projectTag);
      // Success: close the dialog. The success banner is rendered on the
      // Reports page itself (by ReportInputTable's setUploadMessage).
      closeAndReset();
    } catch (err) {
      // Failure: stay open so the user can read the error and retry without
      // re-picking the file.
      setErrorMessage(err?.message || "Upload failed.");
      setIsUploading(false);
      setPhase(null);
    }
  };

  // Block all close paths (backdrop click, ESC) while uploading.
  const handleDialogClose = (_event, reason) => {
    if (isUploading) return;
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      closeAndReset();
      return;
    }
    closeAndReset();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isUploading}
    >
      <DialogTitle>Upload Input File</DialogTitle>
      <DialogContent>
        {/* Drag & Drop Zone */}
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: "2px dashed #1976d2",
            borderRadius: "8px",
            textAlign: "center",
            padding: "20px",
            backgroundColor: isDragging ? "#f0f8ff" : "#fafafa",
            cursor: isUploading ? "not-allowed" : "pointer",
            opacity: isUploading ? 0.6 : 1,
          }}
          onClick={() => {
            if (!isUploading) fileInputRef.current.click();
          }}
        >
          <Typography variant="body1" color="textSecondary">
            {file ? `Selected: ${file.name}` : "Drag & drop a file here or click to select"}
          </Typography>
        </Box>

        {/* Hidden File Input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
          disabled={isUploading}
        />

        <ProjectTagField
          value={projectTag}
          onChange={setProjectTag}
          tags={projectTags?.tags}
          available={projectTags?.available}
          staff={projectTags?.staff}
          projects={projectTags?.projects}
        />

        {/* Phase indicator — visible only while the request is in flight. */}
        {isUploading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="textSecondary">
              {PHASE_TEXT[phase] || "Working…"}
            </Typography>
          </Box>
        )}

        {/* Inline error — stays in the dialog so the user can retry. */}
        {errorMessage && !isUploading && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setErrorMessage("")}>
            {errorMessage}
          </Alert>
        )}

        {!isUploading && !errorMessage && (
          <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 2 }}>
            Uploaded files are automatically added to your Input Library.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={closeAndReset} color="primary" disabled={isUploading}>
          Cancel
        </Button>
        <Button onClick={handleUpload} color="primary" disabled={!file || isUploading}>
          {isUploading ? <CircularProgress size={20} /> : errorMessage ? "Retry" : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
